/**
 * RecipientShareDialog — simplified sharing dialog for external partner org viewers.
 *
 * Shown to users with `canManageOrgSharing === true` (i.e. accepted audience-invite
 * collaborators). Allows them to control how broadly the audience is visible within
 * their own organization via the org-grant scope.
 *
 * When scope='specific', the user can pick individual org members who get access.
 * Switching away from 'specific' clears the member list (by design — re-add on return).
 *
 * Only the "Access" tab is shown. Distribution and Marketplace tabs are hidden because
 * recipient orgs cannot further distribute an audience they don't own.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Building, Lock, Users, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'
import { populationsApi, organizationsApi } from '@/lib/api'
import { ShareModalShell } from '@/components/sharing/ShareModalShell'
import { MemberSearchInput } from '@/components/sharing/MemberSearchInput'
import type { OrgMemberOption } from '@/components/sharing/MemberSearchInput'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'

type OrgGrantScope = 'org' | 'specific' | 'revoked'

interface SpecificMember {
  id: string
  name: string | null
  email: string | null
}

interface RecipientShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  audienceId: string
  audienceName: string
  /** Current org-grant scope for this audience in the viewer's org. */
  currentScope?: OrgGrantScope
}

const SCOPE_OPTION_KEYS: Array<{ value: OrgGrantScope; labelKey: 'onlyMe' | 'specificPeople' | 'myOrganization'; icon: typeof Lock }> = [
  {
    value: 'revoked',
    labelKey: 'onlyMe',
    icon: Lock,
  },
  {
    value: 'specific',
    labelKey: 'specificPeople',
    icon: Users,
  },
  {
    value: 'org',
    labelKey: 'myOrganization',
    icon: Building,
  },
]

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function RecipientShareDialog({
  open,
  onOpenChange,
  audienceId,
  audienceName,
  currentScope = 'revoked',
}: RecipientShareDialogProps) {
  const { t } = useTranslation('app')
  const { org } = useCurrentOrg()
  const [scope, setScope] = useState<OrgGrantScope>(currentScope)
  const [saving, setSaving] = useState(false)

  // Org members for the picker (loaded once on open)
  const [allOrgMembers, setAllOrgMembers] = useState<Array<OrgMemberOption>>([])
  // Current specific-grant members
  const [specificMembers, setSpecificMembers] = useState<Array<SpecificMember>>([])
  const [membersLoading, setMembersLoading] = useState(false)

  useEffect(() => {
    setScope(currentScope)
  }, [currentScope])

  // Load org members and current specific-member list when dialog opens or scope becomes 'specific'
  useEffect(() => {
    if (!open || !org?.id) return

    organizationsApi.getMembers(org.id).then((raw) => {
      setAllOrgMembers(
        raw.map((m) => ({
          userId: m.id,
          name: m.name || m.email || t('unknown', { ns: 'common' }),
          email: m.email || '',
          initials: getInitials(m.name || m.email || '??'),
          avatarSeed: null,
        })),
      )
    }).catch(() => {})

    if (scope === 'specific') {
      setMembersLoading(true)
      populationsApi.listOrgGrantMembers(audienceId)
        .then((rows) => setSpecificMembers(rows.map(({ id, name, email }) => ({ id, name, email }))))
        .catch(() => {})
        .finally(() => setMembersLoading(false))
    }
  }, [open, org?.id, scope, audienceId])

  const handleScopeChange = useCallback(async (newScope: OrgGrantScope) => {
    if (newScope === scope) return
    setSaving(true)
    try {
      await populationsApi.setOrgGrantScope(audienceId, newScope)
      setScope(newScope)
      // Clear member list when leaving 'specific' (server deletes rows too).
      if (newScope !== 'specific') {
        setSpecificMembers([])
      } else {
        // Switching TO 'specific' — load any existing members.
        setMembersLoading(true)
        populationsApi.listOrgGrantMembers(audienceId)
          .then((rows) => setSpecificMembers(rows.map(({ id, name, email }) => ({ id, name, email }))))
          .catch(() => {})
          .finally(() => setMembersLoading(false))
      }
      const messages: Record<OrgGrantScope, string> = {
        org: t('sharing.sharedWithOrg'),
        specific: t('sharing.limitedMembers'),
        revoked: t('sharing.visibleOnlyYou'),
      }
      toast.success(messages[newScope])
    } catch {
      toast.error(t('sharing.settingsFailed'))
    } finally {
      setSaving(false)
    }
  }, [audienceId, scope, t])

  const handleAddMember = useCallback(async (member: OrgMemberOption) => {
    try {
      await populationsApi.addOrgGrantMember(audienceId, member.userId)
      setSpecificMembers((prev) => [
        ...prev,
        { id: member.userId, name: member.name, email: member.email },
      ])
    } catch {
      toast.error(t('sharing.addMemberFailed'))
    }
  }, [audienceId])

  const handleRemoveMember = useCallback(async (userId: string) => {
    try {
      await populationsApi.removeOrgGrantMember(audienceId, userId)
      setSpecificMembers((prev) => prev.filter((m) => m.id !== userId))
    } catch {
      toast.error(t('sharing.removeMemberFailed'))
    }
  }, [audienceId])

  const specificMemberIds = useMemo(
    () => specificMembers.map((m) => m.id),
    [specificMembers],
  )

  return (
    <ShareModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('sharing.shareNamed', { name: audienceName })}
      tabs={[{ id: 'access', label: t('sharing.access') }]}
      activeTab="access"
      onTabChange={() => {}}
    >
      <div className="flex flex-col gap-4 px-6 py-5">
        <Text size="sm" color="muted">
          Control who in your organization can see this audience.
        </Text>

        <div className="flex flex-col gap-2">
          {SCOPE_OPTION_KEYS.map((option) => {
            const Icon = option.icon
            const isSelected = scope === option.value
            const descriptions: Record<OrgGrantScope, string> = {
              revoked: 'Only you can see this audience in your workspace',
              specific: 'Only org members you explicitly invite can see this',
              org: 'Everyone at your organization can see this audience',
            }
            return (
              <button
                key={option.value}
                type="button"
                disabled={saving}
                onClick={() => handleScopeChange(option.value)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border border-border px-3 py-3 text-left transition-colors',
                  'hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected && 'bg-muted/30 border-foreground/20',
                )}
                data-testid={`recipient-scope-${option.value}`}
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-sm font-medium text-foreground">{t(`sharing.${option.labelKey}`)}</span>
                  <span className="text-xs text-muted-foreground mt-0.5">{descriptions[option.value]}</span>
                </div>
                {isSelected && (
                  <div className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-foreground" />
                )}
              </button>
            )
          })}
        </div>

        {/* Member picker — only visible when scope=specific */}
        {scope === 'specific' && (
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 px-4 py-4">
            <Text size="sm" weight="medium">{t('sharing.addPeopleOrg')}</Text>

            <MemberSearchInput
              members={allOrgMembers}
              excludeUserIds={specificMemberIds}
              onSelect={handleAddMember}
              placeholder={t('sharing.searchNameEmail')}
              addLabel={t('add', { ns: 'common' })}
            />

            {membersLoading && (
              <Text size="sm" color="muted">{t('loading', { ns: 'common' })}</Text>
            )}

            {!membersLoading && specificMembers.length === 0 && (
              <Text size="sm" color="muted">{t('sharing.noMembersYet')}</Text>
            )}

            {specificMembers.length > 0 && (
              <ul className="flex flex-col gap-1">
                {specificMembers.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/40"
                  >
                    <UserAvatar
                      name={member.name || member.email || '?'}
                      initials={getInitials(member.name || member.email || '?')}
                      size="xs"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      {member.name && (
                        <span className="truncate text-sm font-medium text-foreground">
                          {member.name}
                        </span>
                      )}
                      {member.email && (
                        <span className="truncate text-xs text-muted-foreground">
                          {member.email}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member.id)}
                      className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={`Remove ${member.name || member.email}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </ShareModalShell>
  )
}
